/**
 * FileUploader Component
 *
 * Reusable file upload component for resources (videos, documents, images).
 * Supports both local file upload and Google Drive import.
 */

import { useRef, useState } from 'react';
import { Upload, X, Loader, Video, FileText, Image as ImageIcon, File } from 'lucide-react';

import { GoogleDrivePicker } from './GoogleDrivePicker';

import { Button } from '@/components/ui/button';
import { uploads } from '@/services/api/admin';


// File type icons based on content type
const getFileIcon = (contentType) => {
  if (!contentType) return File;
  if (contentType.startsWith('video/')) return Video;
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('word')) return FileText;
  return File;
};

// Accepted file types for input
const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'application/pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx'
].join(',');

/**
 * FileUploader - Upload resource files (videos, documents, images)
 *
 * @param {Object} props
 * @param {string} [props.currentFile] - Current file URL
 * @param {string} [props.currentFileName] - Current file name
 * @param {string} [props.currentContentType] - Current file content type
 * @param {Function} props.onUpload - Callback with upload result {url, resource_type, filename, content_type}
 * @param {Function} [props.onRemove] - Callback when file is removed
 * @param {string} [props.label] - Button label
 * @param {string} [props.className] - Additional CSS classes
 */
export function FileUploader({
  currentFile,
  currentFileName,
  currentContentType,
  onUpload,
  onRemove,
  label = 'Upload File',
  className = '',
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Use pre-signed URL for direct S3 upload (bypasses API Gateway 10MB limit)
      // Step 1: Request pre-signed upload URL from backend
      setUploadProgress(10);
      const uploadUrlData = await uploads.getResourceUploadUrl(file.name, file.type);

      // Step 2: Upload directly to S3 using pre-signed URL
      setUploadProgress(20);
      const uploadResponse = await fetch(uploadUrlData.upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      setUploadProgress(80);

      // Step 3: Notify backend that upload is complete (for thumbnail generation)
      const completionData = await uploads.completeResourceUpload(
        uploadUrlData.cloudfront_url,
        file.name,
        file.type
      );

      setUploadProgress(100);

      onUpload({
        url: completionData.url,
        resource_type: completionData.resource_type,
        filename: completionData.filename || file.name,
        content_type: completionData.content_type || file.type,
        thumbnail_url: completionData.thumbnail_url,
      });
    } catch (err) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    } else {
      onUpload({ url: '', resource_type: '', filename: '', content_type: '' });
    }
  };

  const FileIcon = getFileIcon(currentContentType);

  return (
    <div className={className}>
      {currentFile && (
        <div className="relative mb-4 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-background rounded-lg border">
              <FileIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {currentFileName || 'Uploaded file'}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {currentContentType || 'Unknown type'}
              </p>
              <a
                href={currentFile}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline truncate block"
              >
                View file
              </a>
            </div>
            {onRemove !== false && (
              <button
                type="button"
                onClick={handleRemove}
                className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Upload buttons row */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex-1"
        >
          {uploading ? (
            <>
              <Loader className="h-4 w-4 mr-2 animate-spin" />
              Uploading... {uploadProgress}%
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              {label}
            </>
          )}
        </Button>

        <GoogleDrivePicker
          onFileSelected={onUpload}
          onError={(err) => setError(err)}
          disabled={uploading}
          className="flex-1"
        />
      </div>

      {uploading && (
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-200"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        onChange={handleFileSelect}
        className="hidden"
      />

      <p className="text-xs text-gray-500 mt-2">
        Supported: Images (up to 5GB), Videos (up to 5GB), PDFs & Documents (up to 5GB)
      </p>

      {error && (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      )}
    </div>
  );
}

export default FileUploader;
